import React from 'react';
import { motion } from 'framer-motion';
import { UsersIcon, MegaphoneIcon, BuildingIcon, ShieldCheckIcon, LineChartIcon } from 'lucide-react';
const phases = [{
  title: 'Executive Team Training',
  desc: 'Aligning leadership on AI capabilities and strategic implementation.',
  icon: UsersIcon
}, {
  title: 'Sales & Marketing',
  desc: 'Quick wins in revenue-generating departments. Utilize AI to improve demand generation, sales interaction, reporting, win rate, and client retention.',
  icon: MegaphoneIcon
}, {
  title: 'Company-wide Rollout',
  desc: 'Integrate AI to reduce G&A and improve operating efficiency across all company divisions.',
  icon: BuildingIcon
}, {
  title: 'Certification',
  desc: 'Workforce AI fluency and certification training for all employees on utilizing AI and the specific tools we have built for you.',
  icon: ShieldCheckIcon
}, {
  title: 'AI Dashboards & Tracking',
  desc: 'Dashboards, skills library, efficiency tracking, and technology you need to monitor your updated operations.',
  icon: LineChartIcon
}];
export function ApproachSection() {
  return <section id="approach" className="py-24 md:py-32 bg-foreground text-white overflow-hidden" data-id="element-1">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-2">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-3">
          <h2 className="text-3xl md:text-5xl font-serif mb-6" data-id="element-4">Our Approach</h2>
          <p className="text-lg text-neutral-400" data-id="element-5">
            A systematic, top-down implementation that ensures adoption and
            measurable results.
          </p>
        </div>

        <div className="relative max-w-4xl mx-auto" data-id="element-6">
          {/* Connecting Line */}
          <div className="absolute top-0 bottom-0 left-[27px] md:left-1/2 w-0.5 bg-neutral-800 md:-translate-x-1/2" data-id="element-7" />

          <motion.div className="absolute top-0 left-[27px] md:left-1/2 w-0.5 bg-accent md:-translate-x-1/2 origin-top" initial={{
          scaleY: 0
        }} whileInView={{
          scaleY: 1
        }} transition={{
          duration: 2,
          ease: 'easeInOut'
        }} viewport={{
          once: true,
          margin: '-100px'
        }} data-id="element-8" />

          <div className="space-y-12 md:space-y-0 relative z-10" data-id="element-9">
            {phases.map((phase, i) => {
            const isEven = i % 2 === 0;
            const Icon = phase.icon;
            return <div key={i} className="relative flex items-center md:justify-between md:h-32" data-id="element-10">
                  {/* Mobile Layout */}
                  <div className="md:hidden flex gap-6 w-full" data-id="element-11">
                    <div className="relative flex-shrink-0 w-14 h-14 rounded-full bg-neutral-900 border-2 border-neutral-800 flex items-center justify-center z-10" data-id="element-12">
                      <Icon className="w-5 h-5 text-accent" data-id="element-13" />
                    </div>
                    <motion.div className="pt-2 pb-8" initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  duration: 0.5,
                  delay: 0.2
                }} viewport={{
                  once: true
                }} data-id="element-14">
                      <div className="text-xs font-bold text-accent mb-2 uppercase tracking-wider" data-id="element-15">
                        Phase {i + 1}
                      </div>
                      <h3 className="text-xl font-serif mb-2" data-id="element-16">{phase.title}</h3>
                      <p className="text-sm text-neutral-400" data-id="element-17">{phase.desc}</p>
                    </motion.div>
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden md:flex w-full items-center" data-id="element-18">
                    <motion.div className={`w-1/2 pr-12 text-right ${!isEven ? 'order-1 opacity-0' : 'order-1'}`} initial={isEven ? {
                  opacity: 0,
                  x: -20
                } : {}} whileInView={isEven ? {
                  opacity: 1,
                  x: 0
                } : {}} transition={{
                  duration: 0.5,
                  delay: 0.2
                }} viewport={{
                  once: true
                }} data-id="element-19">
                      {isEven && <>
                          <div className="text-xs font-bold text-accent mb-2 uppercase tracking-wider" data-id="element-20">
                            Phase {i + 1}
                          </div>
                          <h3 className="text-2xl font-serif mb-2" data-id="element-21">
                            {phase.title}
                          </h3>
                          <p className="text-sm text-neutral-400 ml-auto max-w-xs" data-id="element-22">
                            {phase.desc}
                          </p>
                        </>}
                    </motion.div>

                    <div className="absolute left-1/2 -translate-x-1/2 w-14 h-14 rounded-full bg-neutral-900 border-2 border-neutral-800 flex items-center justify-center z-10" data-id="element-23">
                      <Icon className="w-5 h-5 text-accent" data-id="element-24" />
                    </div>

                    <motion.div className={`w-1/2 pl-12 ${isEven ? 'order-3 opacity-0' : 'order-3'}`} initial={!isEven ? {
                  opacity: 0,
                  x: 20
                } : {}} whileInView={!isEven ? {
                  opacity: 1,
                  x: 0
                } : {}} transition={{
                  duration: 0.5,
                  delay: 0.2
                }} viewport={{
                  once: true
                }} data-id="element-25">
                      {!isEven && <>
                          <div className="text-xs font-bold text-accent mb-2 uppercase tracking-wider" data-id="element-26">
                            Phase {i + 1}
                          </div>
                          <h3 className="text-2xl font-serif mb-2" data-id="element-27">
                            {phase.title}
                          </h3>
                          <p className="text-sm text-neutral-400 max-w-xs" data-id="element-28">
                            {phase.desc}
                          </p>
                        </>}
                    </motion.div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}